import { useState, useRef, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '../components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import {
  Upload, Download, FileSpreadsheet, FileText, CheckCircle2,
  AlertCircle, XCircle, Loader2, Info, Users, ArrowRight, ArrowLeft,
  UserPlus, Tags, Package
} from 'lucide-react';
import api, { usersAPI, segmentsAPI, productLinesAPI, contactsAPI } from '../api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactImportExportPage = () => {
  const { hasRole } = useAuth();
  const fileInputRef = useRef(null);
  
  // Export state
  const [exporting, setExporting] = useState(false);
  
  // Import state
  const [importing, setImporting] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  
  // Post-import assignment state
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignLoading, setAssignLoading] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [segments, setSegments] = useState([]);
  const [productLines, setProductLines] = useState([]);
  const [assignData, setAssignData] = useState({
    assigned_to: '',
    segments: [],
    product_lines: [],
    mode: 'add'
  });
  const [importedContactIds, setImportedContactIds] = useState([]);

  // Fetch data for assignment dialog
  useEffect(() => {
    const fetchAssignmentData = async () => {
      try {
        const [usersRes, segmentsRes, plRes] = await Promise.all([
          usersAPI.getTeam(),
          segmentsAPI.getAll(),
          productLinesAPI.getAll()
        ]);
        setTeamMembers(usersRes.data || []);
        setSegments(segmentsRes.data?.segments || []);
        setProductLines(plRes.data || []);
      } catch (error) {
        console.error('Failed to fetch assignment data');
      }
    };
    fetchAssignmentData();
  }, []);

  // Handle post-import assignment
  const handleAssignImported = async () => {
    const hasAssignment = assignData.assigned_to || 
                          assignData.segments.length > 0 || 
                          assignData.product_lines.length > 0;
    
    if (!hasAssignment) {
      toast.error('Please select at least one assignment option');
      return;
    }
    
    setAssignLoading(true);
    try {
      const payload = {
        contact_ids: importedContactIds,
        mode: assignData.mode
      };
      
      if (assignData.assigned_to) payload.assigned_to = assignData.assigned_to;
      if (assignData.segments.length > 0) payload.segments = assignData.segments;
      if (assignData.product_lines.length > 0) payload.product_lines = assignData.product_lines;
      
      const result = await contactsAPI.bulkAssign(payload);
      toast.success(`${result.data.modified_count} imported contacts assigned successfully!`);
      setShowAssignDialog(false);
      setAssignData({ assigned_to: '', segments: [], product_lines: [], mode: 'add' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign contacts');
    } finally {
      setAssignLoading(false);
    }
  };

  // Export functions
  const handleExport = async (format) => {
    try {
      setExporting(true);
      const endpoint = format === 'csv' ? '/contacts/export/csv' : '/contacts/export/excel';
      
      const response = await api.get(endpoint, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contacts_export_${new Date().toISOString().split('T')[0]}.${format === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Contacts exported to ${format.toUpperCase()} successfully!`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export contacts');
    } finally {
      setExporting(false);
    }
  };

  // Download template
  const handleDownloadTemplate = async (format) => {
    try {
      const response = await api.get(`/contacts/import/template?format=${format}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contact_import_template.${format === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  // File selection handlers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileName = file.name.toLowerCase();
    const isValid = validTypes.some(type => fileName.endsWith(type));
    
    if (!isValid) {
      toast.error('Please select a CSV or Excel file');
      return;
    }
    
    setSelectedFile(file);
    setImportResult(null);
  };

  // Import function
  const handleImport = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setImporting(true);
      setImportResult(null);
      
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await api.post('/contacts/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setImportResult(response.data);
      
      if (response.data.imported > 0) {
        toast.success(`Successfully imported ${response.data.imported} contacts!`);
      } else if (response.data.skipped_duplicates > 0) {
        toast.info('All contacts were duplicates and skipped');
      } else {
        toast.warning('No contacts were imported');
      }
    } catch (error) {
      console.error('Import failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to import contacts');
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setSelectedFile(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (!hasRole(['admin', 'sales_manager', 'team_lead', 'sales_rep'])) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="p-8 text-center">
          <Users className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
          <p className="text-muted-foreground">You don&apos;t have permission to import/export contacts.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="import-export-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6" />
          Import & Export Contacts
        </h1>
        <p className="text-muted-foreground">
          Bulk import contacts from CSV/Excel files or export your contact database
        </p>
      </div>

      <Tabs defaultValue="import" className="space-y-6">
        <TabsList>
          <TabsTrigger value="import" data-testid="import-tab">
            <Upload className="h-4 w-4 mr-2" />
            Import Contacts
          </TabsTrigger>
          <TabsTrigger value="export" data-testid="export-tab">
            <Download className="h-4 w-4 mr-2" />
            Export Contacts
          </TabsTrigger>
        </TabsList>

        {/* Import Tab */}
        <TabsContent value="import" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Import Area */}
            <div className="lg:col-span-2 space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Upload Contacts File</CardTitle>
                  <CardDescription>
                    Upload a CSV or Excel file containing your contacts. Duplicates (matching email) will be skipped.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* File Drop Zone */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
                      ${dragOver ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-slate-300'}
                      ${selectedFile ? 'bg-green-50 border-green-300' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="file-drop-zone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="hidden"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                      data-testid="file-input"
                    />
                    
                    {selectedFile ? (
                      <div className="space-y-2">
                        <CheckCircle2 className="h-12 w-12 mx-auto text-green-500" />
                        <p className="font-medium text-green-700">{selectedFile.name}</p>
                        <p className="text-sm text-green-600">
                          {(selectedFile.size / 1024).toFixed(1)} KB
                        </p>
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); resetImport(); }}>
                          Choose Different File
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="h-12 w-12 mx-auto text-slate-400" />
                        <p className="font-medium">Drop your file here or click to browse</p>
                        <p className="text-sm text-muted-foreground">
                          Supports CSV, XLSX, XLS files
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Import Button */}
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handleImport}
                    disabled={!selectedFile || importing}
                    data-testid="import-btn"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Import Contacts
                      </>
                    )}
                  </Button>

                  {/* Import Results */}
                  {importResult && (
                    <div className="space-y-4 pt-4 border-t" data-testid="import-results">
                      <h4 className="font-semibold">Import Results</h4>
                      
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="bg-slate-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold">{importResult.total_rows}</p>
                          <p className="text-xs text-muted-foreground">Total Rows</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-green-600">{importResult.imported}</p>
                          <p className="text-xs text-green-600">Imported</p>
                        </div>
                        <div className="bg-amber-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-amber-600">{importResult.skipped_duplicates}</p>
                          <p className="text-xs text-amber-600">Duplicates Skipped</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-red-600">{importResult.skipped_invalid}</p>
                          <p className="text-xs text-red-600">Invalid Skipped</p>
                        </div>
                      </div>

                      {/* Success/Warning Messages */}
                      {importResult.imported > 0 && (
                        <Alert className="bg-green-50 border-green-200">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <AlertTitle className="text-green-800">Import Successful</AlertTitle>
                          <AlertDescription className="text-green-700">
                            {importResult.imported} contacts have been added to your CRM.
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Assign Imported Contacts Button */}
                      {importResult.imported > 0 && importResult.imported_ids?.length > 0 && (
                        <div className="flex gap-3">
                          <Button 
                            variant="outline" 
                            onClick={() => {
                              setImportedContactIds(importResult.imported_ids);
                              setShowAssignDialog(true);
                            }}
                            data-testid="assign-imported-btn"
                          >
                            <UserPlus className="h-4 w-4 mr-2" />
                            Assign Imported Contacts
                          </Button>
                          <Button variant="outline" asChild>
                            <Link to="/contacts">
                              View All Contacts
                            </Link>
                          </Button>
                        </div>
                      )}

                      {/* Errors */}
                      {importResult.errors && importResult.errors.length > 0 && (
                        <Alert variant="destructive">
                          <AlertCircle className="h-4 w-4" />
                          <AlertTitle>Import Errors</AlertTitle>
                          <AlertDescription>
                            <ul className="list-disc list-inside text-sm mt-2 space-y-1">
                              {importResult.errors.map((error, idx) => (
                                <li key={idx}>{error}</li>
                              ))}
                            </ul>
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Import Guide Sidebar */}
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Import Guide</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold">
                        1
                      </div>
                      <div>
                        <p className="font-medium text-sm">Download Template</p>
                        <p className="text-xs text-muted-foreground">Start with our template to ensure correct format</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold">
                        2
                      </div>
                      <div>
                        <p className="font-medium text-sm">Add Your Data</p>
                        <p className="text-xs text-muted-foreground">Fill in contact details (email is required)</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold">
                        3
                      </div>
                      <div>
                        <p className="font-medium text-sm">Upload & Import</p>
                        <p className="text-xs text-muted-foreground">Duplicates are automatically skipped</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <p className="text-sm font-medium mb-2">Download Template</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => handleDownloadTemplate('csv')}>
                        <FileText className="h-4 w-4 mr-1" />
                        CSV
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => handleDownloadTemplate('excel')}>
                        <FileSpreadsheet className="h-4 w-4 mr-1" />
                        Excel
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Supported Fields</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive" className="text-xs">Required</Badge>
                      <span className="text-sm">email</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="destructive" className="text-xs">Required</Badge>
                      <span className="text-sm">first_name or last_name</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-3 pt-3 border-t">
                      <p className="font-medium mb-1">Optional Fields:</p>
                      <p>phone, job_title, status, source, notes, tags, segments</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Duplicate Handling</AlertTitle>
                <AlertDescription className="text-xs">
                  Contacts with emails that already exist in your CRM will be automatically skipped.
                </AlertDescription>
              </Alert>
            </div>
          </div>
        </TabsContent>

        {/* Export Tab */}
        <TabsContent value="export" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* CSV Export */}
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <FileText className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <CardTitle>Export to CSV</CardTitle>
                    <CardDescription>Universal format for spreadsheets</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                  <li>• Compatible with Excel, Google Sheets, Numbers</li>
                  <li>• Smallest file size</li>
                  <li>• Best for data migration</li>
                </ul>
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => handleExport('csv')}
                  disabled={exporting}
                  data-testid="export-csv-btn"
                >
                  {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                  Download CSV
                </Button>
              </CardContent>
            </Card>

            {/* Excel Export */}
            <Card className="hover:shadow-md transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-emerald-100 rounded-lg">
                    <FileSpreadsheet className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle>Export to Excel</CardTitle>
                    <CardDescription>Native Microsoft Excel format</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <ul className="text-sm text-muted-foreground space-y-1 mb-4">
                  <li>• Native Excel formatting support</li>
                  <li>• Best for reporting and analysis</li>
                  <li>• Preserves data types</li>
                </ul>
                <Button
                  className="w-full"
                  variant="outline"
                  onClick={() => handleExport('excel')}
                  disabled={exporting}
                  data-testid="export-excel-btn"
                >
                  {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
                  Download Excel
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Export Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Export Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium mb-2">Fields Included</h4>
                  <div className="flex flex-wrap gap-1">
                    {['ID', 'First Name', 'Last Name', 'Email', 'Phone', 'Job Title', 'Status', 'Source', 'Notes', 'Tags', 'Segments', 'Product Lines', 'AI Score', 'Created At'].map(field => (
                      <Badge key={field} variant="secondary" className="text-xs">{field}</Badge>
                    ))}
                  </div>
                </div>
                <div>
                  <h4 className="font-medium mb-2">What&apos;s Exported</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• All contacts in your database</li>
                    <li>• Tags and segments as comma-separated values</li>
                    <li>• AI health scores (if available)</li>
                    <li>• Timestamps in ISO format</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Post-Import Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={(open) => {
        setShowAssignDialog(open);
        if (!open) setAssignData({ assigned_to: '', segments: [], product_lines: [], mode: 'add' });
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Assign {importedContactIds.length} Imported Contact(s)
            </DialogTitle>
            <DialogDescription>
              Quickly assign all imported contacts to a team member, segments, or product lines.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Assignment Mode */}
            <div className="space-y-3">
              <Label>Assignment Mode</Label>
              <RadioGroup 
                value={assignData.mode} 
                onValueChange={(v) => setAssignData(p => ({ ...p, mode: v }))}
                className="flex gap-4"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="add" id="import-mode-add" />
                  <Label htmlFor="import-mode-add" className="font-normal cursor-pointer">
                    Add to existing
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="replace" id="import-mode-replace" />
                  <Label htmlFor="import-mode-replace" className="font-normal cursor-pointer">
                    Replace existing
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Assign to Team Member */}
            <div className="space-y-2">
              <Label>Assign to Team Member</Label>
              <Select
                value={assignData.assigned_to}
                onValueChange={(v) => setAssignData(p => ({ ...p, assigned_to: v === 'none' ? '' : v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select team member..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No assignment</SelectItem>
                  {teamMembers.map(member => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.full_name || member.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Assign Segments */}
            <div className="space-y-2">
              <Label>Assign Segments</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg min-h-[50px]">
                {segments.map(segment => (
                  <Badge
                    key={segment.value}
                    variant={assignData.segments.includes(segment.value) ? 'default' : 'outline'}
                    className="cursor-pointer transition-colors"
                    onClick={() => {
                      setAssignData(p => ({
                        ...p,
                        segments: p.segments.includes(segment.value)
                          ? p.segments.filter(s => s !== segment.value)
                          : [...p.segments, segment.value]
                      }));
                    }}
                  >
                    <Tags className="h-3 w-3 mr-1" />
                    {segment.label}
                  </Badge>
                ))}
                {segments.length === 0 && (
                  <p className="text-sm text-muted-foreground">No segments defined</p>
                )}
              </div>
            </div>

            {/* Assign Product Lines */}
            <div className="space-y-2">
              <Label>Assign Product Lines</Label>
              <div className="flex flex-wrap gap-2 p-3 border rounded-lg min-h-[50px]">
                {productLines.map(pl => (
                  <Badge
                    key={pl.id}
                    variant={assignData.product_lines.includes(pl.id) ? 'default' : 'outline'}
                    className="cursor-pointer transition-colors"
                    onClick={() => {
                      setAssignData(p => ({
                        ...p,
                        product_lines: p.product_lines.includes(pl.id)
                          ? p.product_lines.filter(id => id !== pl.id)
                          : [...p.product_lines, pl.id]
                      }));
                    }}
                  >
                    <Package className="h-3 w-3 mr-1" />
                    {pl.name}
                  </Badge>
                ))}
                {productLines.length === 0 && (
                  <p className="text-sm text-muted-foreground">No product lines defined</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Skip
            </Button>
            <Button onClick={handleAssignImported} disabled={assignLoading}>
              {assignLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Assign Contacts
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContactImportExportPage;
